import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Flower2, Sparkles, Heart, Globe } from 'lucide-react';
import { services } from '../../data/mock';

const iconMap = {
  Flower2: Flower2,
  Sparkles: Sparkles,
  Heart: Heart,
  Globe: Globe,
};

const ServicesPreview = () => {
  const [isVisible, setIsVisible] = useState(false);
  const sectionRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.1 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, []);

  return (
    <section ref={sectionRef} className="py-24 lg:py-32 bg-white relative overflow-hidden">
      {/* Background decorations */}
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute top-1/4 right-0 w-96 h-96 bg-[#E8A5C4]/5 rounded-full blur-3xl" />
        <div className="absolute bottom-1/4 left-0 w-72 h-72 bg-[#C45B9F]/5 rounded-full blur-3xl" />
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
        {/* Header */}
        <div
          className={`text-center mb-16 transition-all duration-1000 ${
            isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
          }`}
        >
          <span className="text-[#C45B9F] font-medium uppercase tracking-wider text-sm">
            Ce que nous offrons
          </span>
          <h2 className="font-heading text-4xl lg:text-5xl text-[#3D2E35] mt-4">
            Nos Services
          </h2>
          <p className="text-[#6B5A62] mt-4 max-w-2xl mx-auto">
            Des prestations sur mesure pour sublimer vos moments les plus précieux
          </p>
        </div>

        {/* Services Grid */}
        <div className="grid md:grid-cols-2 gap-8">
          {services.map((service, index) => {
            const IconComponent = iconMap[service.icon] || Flower2;
            return (
              <div
                key={service.id}
                className={`group p-8 rounded-3xl bg-[#FAF8F5] hover:bg-white border border-transparent hover:border-[#E8A5C4]/30 transition-all duration-500 hover:shadow-xl hover:shadow-[#8B4B7C]/5 ${
                  isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
                }`}
                style={{ transitionDelay: `${index * 150}ms` }}
              >
                <div className="flex items-start space-x-5">
                  <div className="flex-shrink-0">
                    <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-[#E8A5C4] to-[#C45B9F] flex items-center justify-center group-hover:scale-110 transition-transform duration-300">
                      <IconComponent className="text-white" size={28} />
                    </div>
                  </div>
                  <div className="flex-1">
                    <h3 className="font-heading text-xl text-[#3D2E35] group-hover:text-[#8B4B7C] transition-colors duration-300">
                      {service.title}
                    </h3>
                    <p className="text-[#6B5A62] mt-3 leading-relaxed">
                      {service.description}
                    </p>
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* CTA */}
        <div
          className={`text-center mt-12 transition-all duration-1000 delay-500 ${
            isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
          }`}
        >
          <Link
            to="/services"
            className="btn-tevaite-outline inline-flex items-center px-8 py-4 rounded-full font-medium"
          >
            Découvrir tous nos services
            <ArrowRight className="ml-2" size={20} />
          </Link>
        </div>
      </div>
    </section>
  );
};

export default ServicesPreview;
